import React, { useEffect, useMemo, useState } from "react";
import { X, GitCompareArrows, Plus, Minus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const CLASS_STYLE = {
  ADDED:     { label: "Eklendi",  cls: "bg-emerald-50 border-emerald-300 text-emerald-800", dot: "bg-emerald-500" },
  REMOVED:   { label: "Kaldırıldı", cls: "bg-rose-50 border-rose-300 text-rose-800",           dot: "bg-rose-500" },
  CHANGED:   { label: "Değişti",  cls: "bg-amber-50 border-amber-300 text-amber-800",         dot: "bg-amber-500" },
  UNCHANGED: { label: "Değişmedi",cls: "bg-slate-50 border-slate-200 text-slate-500",         dot: "bg-slate-400" },
};

/**
 * Iter 216 §Part H — Deterministic Revision Diff (READ-ONLY).
 *
 * Compares two controlled content revisions by section key. Never mutates
 * revisions, never creates new content. Opens as a modal on top of the
 * Controlled Content Editor.
 */
export const RevisionDiffDialog = ({ open, currentId, onClose }) => {
  const [candidates, setCandidates] = useState([]);
  const [otherId, setOtherId] = useState("");
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);

  useEffect(() => {
    if (!open || !currentId) return;
    setDiff(null); setOtherId("");
    (async () => {
      try {
        const r = await axios.get(
          `${API}/gmp/controlled-content/${currentId}/diff-candidates`, auth());
        setCandidates(r.data.candidates || []);
        if ((r.data.candidates || []).length > 0) {
          setOtherId(r.data.candidates[0].id);
        }
      } catch (e) {
        toast.error("Karşılaştırılabilecek revizyon yüklenemedi");
      }
    })();
  }, [open, currentId]);

  useEffect(() => {
    if (!open || !currentId || !otherId) return;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/gmp/controlled-content/${currentId}/diff/${otherId}`, auth());
        setDiff(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Diff hesaplanamadı");
        setDiff(null);
      } finally { setLoading(false); }
    })();
  }, [otherId, currentId, open]);

  const visibleSections = useMemo(() => {
    if (!diff) return [];
    return (diff.sections || []).filter(s =>
      showUnchanged || s.classification !== "UNCHANGED");
  }, [diff, showUnchanged]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
         data-testid="revision-diff-dialog">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-3 border-b bg-indigo-50">
          <div className="flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-indigo-700" />
            <div>
              <h3 className="text-base font-semibold text-indigo-900">Revizyon Karşılaştırma</h3>
              <div className="text-[11px] text-indigo-800/70">
                Salt-okunur. Karşılaştırma yeni revizyon oluşturmaz, durum değiştirmez.
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-indigo-100 rounded"
                  data-testid="revision-diff-close">
            <X className="w-5 h-5 text-indigo-800" />
          </button>
        </div>

        <div className="px-6 py-3 border-b bg-white flex flex-wrap items-center gap-3">
          <div className="text-xs text-slate-600">Karşılaştırılacak revizyon:</div>
          <select value={otherId} onChange={e => setOtherId(e.target.value)}
                  data-testid="revision-diff-select"
                  className="border rounded px-2 py-1 text-sm min-w-[280px]">
            <option value="">— seçiniz —</option>
            {candidates.map(c => (
              <option key={c.id} value={c.id}>
                {c.master_document_code_snapshot || "—"} · Rev {c.revision_no} · {c.status}
              </option>
            ))}
          </select>
          <label className="text-xs flex items-center gap-1 ml-auto">
            <input type="checkbox" checked={showUnchanged}
                    onChange={e => setShowUnchanged(e.target.checked)}
                    data-testid="revision-diff-show-unchanged" />
            Değişmeyen bölümleri göster
          </label>
        </div>

        {diff && (
          <div className="px-6 py-2 border-b bg-slate-50 flex flex-wrap gap-3 text-xs">
            <span className="font-medium text-slate-600">
              Önceki: <span className="font-mono">{diff.previous.master_document_code || "—"}</span> · Rev {diff.previous.revision_no}
            </span>
            <span className="text-slate-400">→</span>
            <span className="font-medium text-slate-800">
              Şuanki: <span className="font-mono">{diff.current.master_document_code || "—"}</span> · Rev {diff.current.revision_no}
            </span>
            <span className="ml-4 flex gap-2">
              {Object.entries(diff.counts).map(([k, v]) => {
                const st = CLASS_STYLE[k] || CLASS_STYLE.UNCHANGED;
                return (
                  <span key={k} data-testid={`revision-diff-count-${k}`}
                        className={`px-2 py-0.5 rounded border ${st.cls}`}>
                    {st.label}: <span className="font-semibold">{v}</span>
                  </span>
                );
              })}
            </span>
          </div>
        )}

        <div className="flex-1 overflow-auto p-6 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" /> Hesaplanıyor…
            </div>
          )}
          {!loading && !diff && (
            <div className="text-slate-500 text-sm">
              Karşılaştırma için başka bir revizyon seçin. Aynı process_scope
              içinde önceki revizyonlar listelenir.
            </div>
          )}
          {!loading && diff && visibleSections.length === 0 && (
            <div className="text-slate-500 text-sm">
              Değişiklik bulunamadı. "Değişmeyen bölümleri göster"i işaretleyerek
              tüm bölümleri görüntüleyebilirsiniz.
            </div>
          )}
          {!loading && diff && visibleSections.map(sec => {
            const st = CLASS_STYLE[sec.classification] || CLASS_STYLE.UNCHANGED;
            return (
              <div key={sec.section_key}
                   data-testid={`revision-diff-section-${sec.section_key}`}
                   className={`rounded border ${st.cls}`}>
                <div className="px-3 py-2 flex items-center gap-2 border-b border-white/40">
                  <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                  <span className="text-sm font-medium">{sec.section_title}</span>
                  <span className={`ml-auto text-[10px] uppercase font-semibold`}>
                    {st.label}
                  </span>
                </div>
                {sec.classification === "ADDED" && (
                  <pre className="p-3 text-xs whitespace-pre-wrap font-mono text-emerald-900">
                    {sec.current_text || <span className="italic opacity-70">(boş)</span>}
                  </pre>
                )}
                {sec.classification === "REMOVED" && (
                  <pre className="p-3 text-xs whitespace-pre-wrap font-mono text-rose-900 line-through decoration-rose-400/60">
                    {sec.previous_text || <span className="italic opacity-70">(boş)</span>}
                  </pre>
                )}
                {sec.classification === "CHANGED" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                    <div className="p-3 border-r border-amber-200/60">
                      <div className="text-[10px] uppercase font-semibold text-rose-700 flex items-center gap-1 mb-1">
                        <Minus className="w-3 h-3" /> Önceki
                      </div>
                      <pre className="text-xs whitespace-pre-wrap font-mono text-rose-900">
                        {sec.previous_text || <span className="italic opacity-70">(boş)</span>}
                      </pre>
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] uppercase font-semibold text-emerald-700 flex items-center gap-1 mb-1">
                        <Plus className="w-3 h-3" /> Yeni
                      </div>
                      <pre className="text-xs whitespace-pre-wrap font-mono text-emerald-900">
                        {sec.current_text || <span className="italic opacity-70">(boş)</span>}
                      </pre>
                    </div>
                  </div>
                )}
                {sec.classification === "UNCHANGED" && showUnchanged && (
                  <pre className="p-3 text-xs whitespace-pre-wrap font-mono text-slate-600">
                    {sec.current_text || <span className="italic opacity-70">(boş)</span>}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RevisionDiffDialog;
